import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { Loader2, Trophy, Calendar, Users, Activity, ChevronRight, Share2 } from 'lucide-react';
import { isDemoTournament, getDemoTournament, getDemoTeams, getDemoMatches, getDemoPoules } from '../../data/demoData';

const PublicLivePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const outletContext = useOutletContext();
    const isEsports = outletContext?.isEsports || false;

    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ teams: 0, matches: 0, poules: 0 });

    const [sponsors, setSponsors] = useState([]);
    const [currentSponsorIdx, setCurrentSponsorIdx] = useState(0);

    useEffect(() => {
        const fetchTournament = async () => {
            if (isDemoTournament(id)) {
                setTournament(getDemoTournament(id))
                const dTeams = getDemoTeams(id)
                const dMatches = getDemoMatches(id)
                const dPoules = getDemoPoules(id)
                setStats({
                    teams: dTeams.length,
                    matches: dMatches.filter(m => m.status === 'completed').length + dMatches.filter(m => m.status === 'scheduled').length,
                    poules: dPoules.length
                })
                setLoading(false)
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('tournaments')
                    .select('id, name, created_at, settings')
                    .eq('id', id)
                    .single();

                if (error) throw error;
                setTournament(data);

                const [teamsRes, matchesRes, poulesRes] = await Promise.all([
                    supabase.from('teams').select('id', { count: 'exact', head: true }).eq('tournament_id', id),
                    supabase.from('matches').select('id', { count: 'exact', head: true }).eq('tournament_id', id),
                    supabase.from('poules').select('id', { count: 'exact', head: true }).eq('tournament_id', id)
                ]);

                setStats({
                    teams: teamsRes.count || 0,
                    matches: matchesRes.count || 0,
                    poules: poulesRes.count || 0
                });

            } catch (err) {
                console.error("Error fetching public tournament:", err);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchTournament();
    }, [id]);

    // Fetch active sponsors for rotating banner
    useEffect(() => {
        if (!id) return;
        const fetchSponsors = async () => {
            try {
                const { data } = await supabase
                    .from('sponsors')
                    .select('*')
                    .eq('tournament_id', id)
                    .eq('is_active', true);
                
                if (data) setSponsors(data);
            } catch (err) {
                console.error("Error loading public page sponsors:", err);
            }
        };
        fetchSponsors();
    }, [id]);

    // Sponsor rotation interval timer
    useEffect(() => {
        if (sponsors.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentSponsorIdx(prev => (prev + 1) % sponsors.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [sponsors]);

    if (loading) return (
        <div className="flex justify-center p-8 text-white">
            <Loader2 className="animate-spin" size={32} />
        </div>
    );

    if (!tournament) return <div className="p-4 text-center text-white font-bold bg-black/50 rounded m-4">{t('public.error_not_found')}</div>;

    return (
        <div className="p-4 space-y-4 pb-24 max-w-lg mx-auto font-sans">
            {/* Header Card (Stadium / Cyberpunk Style) */}
            <div className={`p-6 rounded-2xl shadow-xl border text-center relative overflow-hidden transition-all duration-300 ${
                isEsports 
                    ? 'bg-[#0a0a14]/90 border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.15)]' 
                    : 'bg-white/90 border-white/20'
            }`}>
                {isEsports ? (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-fuchsia-500 to-cyan-400 animate-pulse"></div>
                ) : (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-indigo-600 header-line"></div>
                )}
                
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg ring-4 ${
                    isEsports 
                        ? 'bg-purple-950/60 ring-purple-500/20 text-white' 
                        : 'bg-gray-900 ring-gray-100 text-white'
                }`}>
                    <Trophy size={28} className={isEsports ? "text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.6)]" : "text-yellow-400"} />
                </div>
                
                <h1 className={`text-3xl font-black mb-1 tracking-tight ${isEsports ? 'text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-cyan-200' : 'text-gray-900'}`}>{tournament.name}</h1>
                
                <p className={`text-xs font-semibold flex items-center justify-center gap-1.5 py-1 px-3.5 rounded-full w-fit mx-auto mt-2.5 transition-colors ${
                    isEsports 
                        ? 'bg-cyan-950/40 border border-cyan-500/20 text-cyan-300 font-mono tracking-wider' 
                        : 'bg-gray-100 text-gray-500'
                }`}>
                    <Calendar size={14} className={isEsports ? "text-cyan-400" : ""} />
                    {new Date(tournament.created_at).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
                <MetricCard label={t('public.metric_teams')} value={stats.teams} icon={Users} color={isEsports ? "text-cyan-400" : "text-indigo-600"} isEsports={isEsports} />
                <MetricCard label={t('public.metric_poules')} value={stats.poules} icon={Activity} color={isEsports ? "text-fuchsia-400" : "text-orange-500"} isEsports={isEsports} />
                <MetricCard label={t('public.metric_matches')} value={stats.matches} icon={Calendar} color={isEsports ? "text-purple-400" : "text-green-600"} isEsports={isEsports} />
            </div>

            {/* Navigation Menu */}
            <div className={`rounded-2xl shadow-lg border overflow-hidden transition-all ${
                isEsports 
                    ? 'bg-[#0a0a14]/90 border-purple-500/20 divide-y divide-purple-500/10' 
                    : 'bg-white/90 border-white/20 divide-y divide-gray-100'
            }`}>
                <MenuLink
                    icon={Trophy}
                    label={t('public.nav_view_standings')}
                    desc={t('public.nav_view_standings_desc')}
                    onClick={() => navigate(`/live/${id}/standings`)}
                    color={isEsports ? "bg-cyan-500/10 border border-cyan-500/20 text-cyan-400" : "bg-indigo-50 text-indigo-600"}
                    isEsports={isEsports}
                />
                <MenuLink
                    icon={Calendar}
                    label={t('public.nav_view_schedule')}
                    desc={t('public.nav_view_schedule_desc')}
                    onClick={() => navigate(`/live/${id}/matches`)}
                    color={isEsports ? "bg-purple-500/10 border border-purple-500/20 text-purple-400" : "bg-orange-50 text-orange-600"}
                    isEsports={isEsports}
                />
            </div>

            {/* Rules / Info Section */}
            {tournament?.settings?.rules && (
                <div className={`backdrop-blur rounded-2xl shadow-lg border p-5 transition-all ${
                    isEsports 
                        ? 'bg-black/40 border-purple-500/20 text-slate-200' 
                        : 'bg-white/90 text-gray-800 border-gray-100'
                }`}>
                    <h3 className={`font-bold text-lg mb-3 flex items-center gap-2 ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                        Toernooi Reglementen & Info
                    </h3>
                    <div className="whitespace-pre-wrap text-sm leading-relaxed opacity-90">
                        {tournament.settings.rules}
                    </div>
                </div>
            )}

            {/* Share Section */}
            <div className={`backdrop-blur rounded-2xl shadow-lg border p-4 transition-all ${
                isEsports 
                    ? 'bg-black/40 border-purple-500/20 text-white' 
                    : 'bg-black/80 text-white border-white/10'
            }`}>
                <h3 className="font-bold text-sm mb-2 flex items-center gap-2"><Share2 size={16} className={isEsports ? "text-purple-400" : ""} /> {t('public.share_title')}</h3>
                <p className={`text-xs mb-3 ${isEsports ? 'text-slate-400' : 'text-gray-400'}`}>{t('public.share_text')}</p>
                <div className={`p-3 rounded-lg text-xs break-all font-mono select-all transition-all cursor-pointer border ${
                    isEsports 
                        ? 'bg-[#12121e]/80 border-purple-500/20 text-cyan-300 hover:bg-[#181829]' 
                        : 'bg-white/10 border-white/10 text-indigo-200 hover:bg-white/20'
                }`}>
                    {window.location.href}
                </div>
            </div>

            {/* Rotating Sponsor Footer Banner */}
            {sponsors && sponsors.length > 0 && (() => {
                const activeSponsor = sponsors[currentSponsorIdx];
                if (!activeSponsor) return null;
                return (
                    <div 
                        onClick={() => activeSponsor.website_url && window.open(activeSponsor.website_url, '_blank')}
                        className={`p-4 rounded-2xl border text-center transition-all duration-300 relative overflow-hidden shadow-lg flex items-center justify-between gap-4 cursor-pointer hover:scale-[1.02] ${
                            isEsports 
                                ? 'bg-[#0a0a14]/90 border-purple-500/20 hover:border-purple-500/40 text-white shadow-[0_0_20px_rgba(16,185,129,0.05)]' 
                                : 'bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100/60 hover:to-teal-100/60 border border-emerald-100 text-gray-900'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-20 bg-white dark:bg-white rounded-lg p-1.5 flex items-center justify-center border border-gray-200/50 dark:border-white/5 overflow-hidden shrink-0">
                                {activeSponsor.logo_url ? (
                                    <img 
                                        src={activeSponsor.logo_url} 
                                        alt={activeSponsor.name} 
                                        className="max-h-full max-w-full object-contain" 
                                    />
                                ) : (
                                    <span className="text-emerald-600 font-extrabold text-[10px] uppercase">{activeSponsor.name.slice(0, 3)}</span>
                                )}
                            </div>
                            <div className="text-left">
                                <span className={`text-[10px] uppercase font-bold tracking-widest block ${
                                    isEsports ? 'text-emerald-400' : 'text-emerald-700'
                                }`}>Partner</span>
                                <span className="font-extrabold text-sm">{activeSponsor.name}</span>
                            </div>
                        </div>

                        {activeSponsor.website_url && (
                            <ChevronRight size={18} className={isEsports ? 'text-emerald-400' : 'text-emerald-600'} />
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

const MetricCard = ({ label, value, icon: Icon, color, isEsports }) => (
    <div className={`p-4 rounded-xl shadow-lg border text-center flex flex-col items-center justify-center transition-all hover:scale-105 ${
        isEsports 
            ? 'bg-[#0a0a14]/80 border-purple-500/15' 
            : 'bg-white/90 border-white/20'
    }`}>
        <Icon size={20} className={`mb-1 ${color} ${isEsports ? 'drop-shadow-[0_0_5px_rgba(168,85,247,0.3)]' : ''}`} />
        <div className={`text-xl font-black ${isEsports ? 'text-white' : 'text-gray-900'}`}>{value}</div>
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</div>
    </div>
);

const MenuLink = ({ icon: Icon, label, desc, onClick, color, isEsports }) => (
    <button onClick={onClick} className={`w-full text-left p-4 transition-colors flex items-center justify-between group ${
        isEsports ? 'hover:bg-purple-500/5' : 'hover:bg-white'
    }`}>
        <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${color} shadow-sm group-hover:scale-110 transition-transform`}>
                <Icon size={22} />
            </div>
            <div>
                <div className={`font-black text-lg transition-colors ${isEsports ? 'text-white group-hover:text-cyan-400' : 'text-gray-900'}`}>{label}</div>
                <div className={`text-xs font-medium ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{desc}</div>
            </div>
        </div>
        <ChevronRight size={20} className={`transition-all group-hover:translate-x-1 ${
            isEsports ? 'text-purple-500 group-hover:text-cyan-400' : 'text-gray-300 group-hover:text-gray-900'
        }`} />
    </button>
);

export default PublicLivePage;
